import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1/portal';
const KNOWLEDGE_API_URL = '/api';

// Portal API client (for tickets, auth, etc.)
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-tenant-id': '11111111-1111-1111-1111-111111111111',
  },
});

// Knowledge API client (for articles, categories, search)
export const knowledgeApiClient: AxiosInstance = axios.create({
  baseURL: KNOWLEDGE_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-tenant-id': '11111111-1111-1111-1111-111111111111',
  },
});

// --- Token refresh state (shared across both clients) ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
};

async function refreshAccessToken(): Promise<string> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post(
    '/api/v1/portal/auth/refresh',
    { refreshToken },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-id': '11111111-1111-1111-1111-111111111111',
      },
    }
  );

  const { token, refreshToken: newRefreshToken } = response.data;
  useAuthStore.getState().setTokens(token, newRefreshToken);
  return token;
}

function forceLogout() {
  try {
    useAuthStore.getState().logout();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  } catch (e) {
    console.warn('Failed to logout:', e);
  }
}

// Setup interceptors for both clients
const setupInterceptors = (client: AxiosInstance) => {
  // Request interceptor to add auth token
  client.interceptors.request.use(
    (reqConfig) => {
      try {
        const token = useAuthStore.getState().token;
        if (token) {
          reqConfig.headers.Authorization = `Bearer ${token}`;
        }
      } catch (e) {
        console.warn('Failed to get auth token:', e);
      }
      return reqConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor with token refresh on 401
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Handle network errors gracefully
      if (!error.response) {
        console.error('Network error:', error.message);
        return Promise.reject(new Error('Network error. Please check your connection.'));
      }

      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Only attempt refresh for 401 errors, and only once per request
      if (error.response.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // Don't try to refresh if there's no refresh token
      const { refreshToken } = useAuthStore.getState();
      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return client(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      // This is the first 401 — start the refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

setupInterceptors(apiClient);
setupInterceptors(knowledgeApiClient);

// Types
export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  parentId: string | null;
  articleCount?: number;
  children?: Category[];
}

export interface Article {
  id: string;
  title: string;
  slug: string;
  content: string;
  contentHtml: string | null;
  excerpt: string | null;
  categoryId: string | null;
  category?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  tags: string[];
  publishedAt: string | null;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SearchResult {
  id: string;
  type: 'article' | 'category';
  title: string;
  slug: string;
  excerpt?: string;
  categoryId?: string;
  categoryName?: string;
}

export interface SearchResults {
  results: SearchResult[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  query: string;
  suggestions?: string[];
}

export interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  channel: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  messages?: Message[];
  assignee?: { id: string; name: string };
}

export interface Message {
  id: string;
  content: string;
  contentType: string;
  senderType: 'customer' | 'agent' | 'system' | 'bot';
  senderName?: string;
  createdAt: string;
  attachments?: Attachment[];
}

export interface Attachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url: string;
}

export interface CreateTicketRequest {
  subject: string;
  description: string;
  categoryId?: string;
  priority?: string;
}

// RAG Types
export interface RagSource {
  id: string;
  type: 'article' | 'document';
  title: string;
  slug?: string;
  relevance: number;
  preview?: string;
}

export interface RagCitation {
  number: number;
  sourceId: string;
  sourceType: 'article' | 'document';
  title: string;
  slug?: string;
}

export interface RagMetadata {
  model: string;
  processingTimeMs: number;
  tokenUsage: {
    prompt: number;
    completion: number;
    total: number;
  };
  contextTruncated: boolean;
  timestamp: string;
}

export interface RagResponse {
  answer: string;
  confidence: number;
  citations: RagCitation[];
  sources: RagSource[];
  metadata: RagMetadata;
}

export interface RagFeedback {
  queryId: string;
  helpful: boolean;
  comment?: string;
}
